/**
 * Flat-scene validator.
 *
 * CMS Scenes published as a standalone `.gltf` document must be flat — every
 * authored object sits directly under the scene root, with no further
 * parenting. This validator rejects any deeper hierarchy so the publish
 * surface stays predictable.
 *
 * The document root (SCENE_ROOT_SPEC D8) is that single top-level node: it is
 * allowed and required, and every other object must be a direct child of it.
 */

import type { SceneObjectData } from '../scenedata/sceneobjectdata';
import { SCENE_ROOT_NAME } from '../core/TraceScene';

export interface FlatSceneOffender {
    /** Node name. */
    name: string;
    /** The parent path that violates the flat-scene rule. */
    parent: string;
}

export interface FlatSceneValidationResult {
    ok: boolean;
    offenders: FlatSceneOffender[];
}

/**
 * Walk scene data and report every node that's nested below the document root.
 *
 * Allowed parent paths:
 *   - `undefined` / `""` — the document root itself, the one parentless record.
 *   - the document root's own name (with or without a leading `/`) — its direct
 *     children. `"Scene"` is still accepted so data assembled the pre-D8 way
 *     (an engine-root prefix) validates unchanged.
 *
 * Anything else (e.g. `"MyScene/Group"`, `"Group/Child"`) is an offender.
 */
export function validateFlatScene(sceneData: SceneObjectData[]): FlatSceneValidationResult {
    const offenders: FlatSceneOffender[] = [];
    const documentRoot = sceneData.find(node => !node.transform?.parent);
    for (const node of sceneData) {
        const parent = node.transform?.parent;
        if (parent && !isRootLevelParent(parent, documentRoot?.name)) {
            offenders.push({ name: node.name, parent });
        }
    }
    return { ok: offenders.length === 0, offenders };
}

/** Format a result for human display (one line per offender). */
export function formatFlatSceneOffenders(offenders: FlatSceneOffender[]): string {
    return offenders
        .map(o => `  • ${o.name} (under ${o.parent})`)
        .join('\n');
}

// ── Internals ─────────────────────────────────────────────────────────

function isRootLevelParent(parent: string, documentRootName?: string): boolean {
    const stripped = parent.startsWith('/') ? parent.slice(1) : parent;
    return stripped === SCENE_ROOT_NAME || (!!documentRootName && stripped === documentRootName);
}
